import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';

const emptyAccount = { client_id: '', broker_name: '', trading_id: '', server_name: '', account_type: 'live', group: '', balance: 0, status: 'active' };

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAccount);
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      const [aRes, cRes] = await Promise.all([api.get('/accounts'), api.get('/clients')]);
      setAccounts(aRes.data);
      setClients(cRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm({...emptyAccount}); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ client_id: a.client_id, broker_name: a.broker_name, trading_id: a.trading_id, server_name: a.server_name || '', account_type: a.account_type, group: a.group || '', balance: a.balance || 0, status: a.status });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, balance: parseFloat(form.balance) || 0 };
      if (editing) { await api.put(`/accounts/${editing.id}`, payload); toast.success('Account updated'); }
      else { await api.post('/accounts', payload); toast.success('Account created'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try { await api.delete(`/accounts/${id}`); toast.success('Account deleted'); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || '-';
  const filtered = accounts.filter(a => a.broker_name?.toLowerCase().includes(search.toLowerCase()) || a.trading_id?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Trading Accounts</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Manage trading accounts per client</p>
        </div>
        <Button onClick={openNew} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="add-account-btn">
          <Plus size={16} className="mr-2" /> Add Account
        </Button>
      </div>
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search accounts..." className="pl-9 bg-white border-brand-border rounded-sm" data-testid="account-search-input" />
      </div>
      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="accounts-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Client', 'Broker', 'Trading ID', 'Server', 'Type', 'Balance', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">No accounts found</td></tr> :
                 filtered.map(a => (
                  <tr key={a.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 text-brand-text-primary">{getClientName(a.client_id)}</td>
                    <td className="py-3 px-4 text-brand-text-primary font-medium">{a.broker_name}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{a.trading_id}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{a.server_name || '-'}</td>
                    <td className="py-3 px-4"><Badge className={`text-xs border-0 ${a.account_type === 'live' ? 'bg-brand-success/15 text-brand-success' : 'bg-blue-100 text-blue-700'}`}>{a.account_type}</Badge></td>
                    <td className="py-3 px-4 font-medium text-brand-text-primary">${Number(a.balance || 0).toLocaleString()}</td>
                    <td className="py-3 px-4"><Badge className={`text-xs border-0 ${a.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>{a.status}</Badge></td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(a)} className="h-8 w-8" data-testid={`edit-account-${a.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(a.id)} className="h-8 w-8 text-brand-error" data-testid={`delete-account-${a.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="account-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? 'Edit Account' : 'New Account'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="account-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Broker Name *</Label><Input value={form.broker_name} onChange={e => setForm({...form, broker_name: e.target.value})} data-testid="account-broker-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Trading ID *</Label><Input value={form.trading_id} onChange={e => setForm({...form, trading_id: e.target.value})} data-testid="account-tradingid-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Server</Label><Input value={form.server_name} onChange={e => setForm({...form, server_name: e.target.value})} className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2">
              <Label>Account Type</Label>
              <Select value={form.account_type} onValueChange={v => setForm({...form, account_type: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="live">Live</SelectItem><SelectItem value="demo">Demo</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Group</Label><Input value={form.group} onChange={e => setForm({...form, group: e.target.value})} className="bg-white border-brand-border rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="account-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
